<script setup>
import { defineProps, ref, nextTick, watch, computed } from 'vue'
// import { useUsersStore } from '../stores/users'
import { Vue3Lottie } from 'vue3-lottie'
import typingAnimation from '../assets/typing_animation.json'
import { marked } from 'marked'
import {
  HandThumbUpIcon as HandThumbUpIconOutline,
  HandThumbDownIcon as HandThumbDownIconOutline,
} from '@heroicons/vue/24/outline'
import {
  HandThumbUpIcon as HandThumbUpIconSolid,
  HandThumbDownIcon as HandThumbDownIconSolid,
} from '@heroicons/vue/24/solid'
import ScrollToBottomButton from './ScrollToBottomButton.vue'

const props = defineProps({
  messages: {
    type: Array,
    required: true,
  },
  sendFeedback: {
    type: Function,
    required: true,
  },
})

const messagesContainerRef = ref(null)
const lastMessageRef = ref(null)     
const isUserScrollingUp = ref(false) 

const handleScroll = () => {
  if (messagesContainerRef.value) {
    const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.value
    isUserScrollingUp.value = scrollHeight - scrollTop > clientHeight + 60
  }
}

// ADDED: This function handles the button click
const handleAdditionalAction = (action) => {
  if (action && action.url) {
    window.open(action.url, '_blank');
  }
};

watch(
  () => props.messages,
  () => {
    nextTick(() => {
      if (!isUserScrollingUp.value && lastMessageRef.value) {
        lastMessageRef.value.scrollIntoView({ behavior: 'smooth', block: 'end' })
      }
    })
  },
  { deep: true },
)

const compMessages = computed(() => {
  return props.messages === null ? [] : props.messages.map((msg) => ({
    ...msg,
    content: marked.parse(msg.content, { breaks: true }),
  }))
})

const scrollToBottom = () => {
  if (messagesContainerRef.value) {
    messagesContainerRef.value.scrollTo({
      top: messagesContainerRef.value.scrollHeight,
      behavior: 'smooth',
    })
    isUserScrollingUp.value = false
  }
}
</script>

<template>
  <div class="relative flex h-full flex-col bg-slate-100">
    <div
      ref="messagesContainerRef"
      class="flex-1 overflow-y-auto"
      @scroll="handleScroll"
    >
      <div
        v-for="(msg, idx) in compMessages"
        :key="msg.message_id || idx"
        :ref="(el) => { if (idx === compMessages.length - 1) lastMessageRef = el }"
        :class="[
          'group mx-auto flex max-w-3xl px-4 lg:px-0',
          msg.role === 'assistant' ? 'py-6' : 'py-2',
        ]"
      >
        <div
          v-if="msg.role === 'assistant'"
          class="flex flex-col items-center justify-between"
        >
          <div
            class="flex h-9 w-9 items-center justify-center rounded-full border border-gray-light font-semibold lg:h-11 lg:w-11"
          >
            <img
              class="h-auto object-center"
              src="/air_mne_eagle_rounded.ico"
              srcset=""
              alt="Air Montenegro"
            />
          </div>
        </div>
        <div
          :id="msg.message_id"
          :class="[
            'relative break-words rounded-[20px] px-4 py-2',
            msg.role === 'user'
              ? 'ml-auto w-fit max-w-[70%] bg-slate-200'
              : 'w-fit max-w-[90%] bg-light-gray',
            idx === 0
              ? 'mt-5'
              : 'mt-0'
          ]"
        >
          <div
            v-if="
              idx == compMessages.length - 1 && msg.content.includes('Loading...')
            "
            class="-mt-3"
          >
            <Vue3Lottie
              :animation-data="typingAnimation"
              :height="50"
              :width="50"
            />
          </div>
          <div
            v-else
            class="max-w-full text-gray-darker [&_li]:py-2 [&_li]:pl-1 [&_ol]:list-decimal [&_ol]:pl-8 [&_ul]:list-disc [&_ul]:pl-5 [&_a]:text-sky-500 [&_pre]:whitespace-pre-wrap"
            v-html="msg.content"
          />
          <div
            v-if="msg.role === 'assistant' && msg.message_id"
            class="static -bottom-6 mt-1 flex w-fit gap-1 rounded-[20px] border border-gray-lighter px-2 py-1 group-hover:flex lg:absolute lg:mt-0 lg:hidden"
          >
            <component
              :is="
                msg.sentiment === 1 || msg.hoveredThumbsUp
                  ? HandThumbUpIconSolid
                  : HandThumbUpIconOutline
              "
              :class="[
                'h-5 w-5 shrink-0 cursor-pointer text-gray',
                msg.sentiment === 1 && !msg.hoveredThumbsUp
                  ? 'text-primary'
                  : 'hover:text-primary',
              ]"
              aria-hidden="true"
              @click="props.sendFeedback(msg, 1)"
            />
            <component
              :is="
                msg.sentiment === -1 || msg.hoveredThumbsDown
                  ? HandThumbDownIconSolid
                  : HandThumbDownIconOutline
              "
              :class="[
                'h-5 w-5 shrink-0 cursor-pointer text-gray',
                msg.sentiment === -1 && !msg.hoveredThumbsDown
                  ? 'text-primary'
                  : 'hover:text-primary',
              ]"
              aria-hidden="true"
              @click="props.sendFeedback(msg, -1)"
            />
          </div>
          <div
            v-if="msg.role === 'assistant' && msg.message_id && msg.additional && msg.additional.message === 'Go to booking'"
            class="mt-2"
          >
            <button
              class="mt-2 px-4 py-2 text-sm font-medium bg-slate text-sky-500 border border-sky-500 rounded-full hover:bg-sky-500 hover:text-white hover:cursor-pointer focus:outline-none transition-colors duration-300 ease-in-out"
              @click="handleAdditionalAction(msg.additional)"
            >
              {{ msg.additional.message }}
            </button>
          </div>
        </div>
      </div>
    </div>
    <ScrollToBottomButton
      :isVisible="isUserScrollingUp"
      class="mb-4 self-end"
      @scroll-to-bottom="scrollToBottom"
    />
  </div>
</template>